import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import Tooltip from '@/components/Tooltip';
import { BORDER_RADIUS, PROFILE_SUMMARY_CHAR_LIMIT } from '@/constants/config';
import { getMemoryThemeColors } from './memoryTheme';

const isWeb = Platform.OS === 'web';
const CHAR_COUNT_WARNING_COLOR = '#F59E0B';

function getCharCountProps(length, limit) {
  const ratio = length / limit;
  if (ratio > 0.9) return { color: 'danger', style: undefined };
  if (ratio > 0.75) return { color: undefined, style: { color: CHAR_COUNT_WARNING_COLOR } };
  return { color: 'secondary', style: undefined };
}

/**
 * MemoryOverviewCard
 *
 * Highlights the synthesized profile summary and lets the user correct it.
 * Edits persist as the starting point for the next dream pass.
 */
function MemoryOverviewCard({
  profileSummary,
  isPending = false,
  isUserEdited = false,
  canEdit = true,
  onSave,
}) {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const memColors = useMemo(() => getMemoryThemeColors(theme, colors), [theme, colors]);

  const styles = useMemo(
    () => createStyles(colors, memColors),
    [colors, memColors]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [draft, setDraft] = useState(profileSummary || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) setDraft(profileSummary || '');
  }, [profileSummary, isEditing]);

  const handleStartEditing = useCallback(() => {
    setDraft(profileSummary || '');
    setIsEditing(true);
  }, [profileSummary]);

  const handleCancelEditing = useCallback(() => {
    if (isSaving) return;
    setDraft(profileSummary || '');
    setIsEditing(false);
  }, [profileSummary, isSaving]);

  const handleSave = useCallback(async () => {
    const next = draft.replace(/\s+/g, ' ').trim();
    if (!next || next.length > PROFILE_SUMMARY_CHAR_LIMIT || isSaving || typeof onSave !== 'function') return;
    setIsSaving(true);
    try {
      const ok = await onSave(next);
      if (ok !== false) setIsEditing(false);
    } catch {
      // Keep the draft so a failed request can be retried.
    } finally {
      setIsSaving(false);
    }
  }, [draft, isSaving, onSave]);

  const handleKeyDown = useCallback((e) => {
    if (!isWeb) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditing();
    }
  }, [handleSave, handleCancelEditing]);

  if (!profileSummary && !isPending && !canEdit) {
    return null;
  }

  const badgeLabel = isEditing
    ? t('pages.memory.profileSummaryEdit')
    : !profileSummary && isPending
      ? t('pages.memory.profileSummaryBadgePending')
      : isUserEdited
        ? t('pages.memory.profileSummaryBadgeUser')
        : profileSummary
          ? t('pages.memory.profileSummaryBadgeAi')
          : t('pages.memory.profileSummaryWrite');

  const bodyText = profileSummary
    ? profileSummary
    : isPending
      ? t('pages.memory.profileSummaryPending')
      : t('pages.memory.profileSummaryEmpty');

  const draftLength = draft.length;
  const charProps = getCharCountProps(draftLength, PROFILE_SUMMARY_CHAR_LIMIT);
  const canSave = Boolean(draft.replace(/\s+/g, ' ').trim()) && draftLength <= PROFILE_SUMMARY_CHAR_LIMIT;

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.iconCircle}>
          <Icon name="wand-magic-sparkles" size={15} color={memColors.accent} />
        </View>

        <View style={styles.headerText}>
          <View style={styles.titleRow}>
            <Text tag="h2" size="n" weight="semibold" color="primary">
              {t('pages.memory.profileSummaryTitle')}
            </Text>
            {(isExpanded || isEditing) && <View style={styles.badge}>
              <Text size="xxs" weight="semibold" style={styles.badgeText}>
                {badgeLabel}
              </Text>
            </View>}
          </View>
        </View>

        {canEdit && !isEditing ? (
          <Tooltip label={profileSummary ? t('pages.memory.profileSummaryEdit') : t('pages.memory.profileSummaryWrite')} showIsXs>
            <Pressable
              style={({ hovered }) => [
                styles.actionButton,
                hovered && styles.actionButtonHovered,
              ]}
              onPress={handleStartEditing}
              accessibilityRole="button"
              accessibilityLabel={profileSummary ? t('pages.memory.profileSummaryEdit') : t('pages.memory.profileSummaryWrite')}
            >
              <Icon name={profileSummary ? 'pen' : 'plus'} size={13} color={colors.text_color_secondary} />
            </Pressable>
          </Tooltip>
        ) : null}
      </View>

      {isEditing ? (
        <View style={styles.editWrap}>
          <TextInput
            style={styles.editInput}
            editable={!isSaving}
            accessibilityLabel={t('pages.memory.profileSummaryEdit')}
            value={draft}
            onChangeText={setDraft}
            multiline
            maxLength={PROFILE_SUMMARY_CHAR_LIMIT + 50}
            autoFocus
            placeholder={t('pages.memory.profileSummaryPlaceholder')}
            placeholderTextColor={colors.text_color_tertiary}
            onKeyPress={handleKeyDown}
          />
          <View style={styles.editMetaRow}>
            <Text size="xxs" color="tertiary">
              {isWeb ? t('pages.memory.saveShortcut') : ''}
            </Text>
            <Text size="xxs" color={charProps.color} style={charProps.style}>
              {draftLength} / {PROFILE_SUMMARY_CHAR_LIMIT}
            </Text>
          </View>
          <View style={styles.editActions}>
            <Button
              text={t('common.cancel')}
              variant="transparent"
              size="medium"
              isDisabled={isSaving}
              onPress={handleCancelEditing}
            />
            <Button
              text={t('pages.memory.profileSummarySave')}
              variant="primary"
              size="medium"
              isDisabled={!canSave}
              isLoading={isSaving}
              onPress={handleSave}
              iconColor={memColors.onInk}
              textStyle={{ color: memColors.onInk, fontWeight: '600' }}
              hoverBackgroundColor={memColors.ink}
              style={{
                backgroundColor: memColors.ink,
                borderRadius: 10,
                opacity: canSave ? 1 : 0.38,
              }}
            />
          </View>
        </View>
      ) : (
        <View style={styles.contentBox}>
          <Text numberOfLines={!isExpanded ? 2 : undefined} size="s" color={profileSummary ? 'primary' : 'secondary'} style={styles.summaryText}>
            {bodyText}
          </Text>
          {(profileSummary || isPending) && (
            <Pressable onPress={() => setIsExpanded(value => !value)} accessibilityRole="button" accessibilityState={{ expanded: isExpanded }} {...(isWeb ? { 'aria-expanded': isExpanded } : {})} style={{ paddingVertical: 10, alignSelf: 'flex-start' }}>
              <Text size="xs" weight="semibold" color="secondary">{t(isExpanded ? 'pages.memory.showLess' : 'pages.memory.showOverview')}</Text>
            </Pressable>
          )}
        </View>
      )}

      {(isExpanded || isEditing) && <View style={styles.footer}>
        <Icon name="circle-info" size={12} color={colors.text_color_secondary} />
        <Text size="xxs" color="tertiary" style={styles.footerText}>
          {t('pages.memory.profileSummaryHint')}
        </Text>
      </View>}
    </View>
  );
}

const createStyles = (colors, memColors) =>
  StyleSheet.create({
    card: {
      padding: 18,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: memColors.border,
      backgroundColor: memColors.surface,
      marginBottom: 28,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginBottom: 8,
    },
    iconCircle: {
      width: 36,
      height: 36,
      borderRadius: 10,
      backgroundColor: memColors.preview,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
    },
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 2,
      paddingHorizontal: 8,
      borderRadius: 6,
      backgroundColor: memColors.preview,
    },
    badgeText: {
      color: colors.text_color_secondary,
    },
    actionButton: {
      padding: 8,
      borderRadius: 8,
      flexShrink: 0,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    actionButtonHovered: {
      backgroundColor: memColors.hover,
    },
    contentBox: {
      paddingVertical: 6,
    },
    summaryText: {
      lineHeight: 22,
    },
    editWrap: {
      gap: 0,
    },
    editInput: {
      backgroundColor: memColors.control,
      borderRadius: BORDER_RADIUS.medium,
      padding: 12,
      width: '100%',
      borderWidth: 1,
      borderColor: memColors.border,
      color: colors.text_color_primary,
      fontSize: 14,
      lineHeight: 21,
      minHeight: 108,
      textAlignVertical: 'top',
      ...(isWeb ? { outlineStyle: 'none' } : {}),
    },
    editMetaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 6,
    },
    editActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 10,
      justifyContent: 'flex-end',
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 8,
    },
    footerText: {
      flex: 1,
    },
  });

export default React.memo(MemoryOverviewCard);
